/**
 * @fileoverview Billing Validation Utilities
 * 
 * This file contains utilities for validating phone number purchases
 * against user billing context and subscription limits.
 */

/**
 * Validate if user can purchase a phone number
 * 
 * @param {BillingContext} billingContext - User's billing context
 * @param {PhoneNumber} phoneNumber - Phone number to purchase
 * @returns {ValidationResult} Validation result with details
 */
export function validatePurchaseEligibility(billingContext, phoneNumber) {
  const result = {
    canPurchase: true,
    warnings: [],
    errors: [],
    costInfo: null,
    requiresPayment: false
  };

  // Check if billing context is available
  if (!billingContext) {
    result.canPurchase = false;
    result.errors.push('Billing information not available. Please contact support.');
    return result;
  }

  // Calculate cost information
  result.costInfo = calculatePurchaseCost(billingContext, phoneNumber);
  result.requiresPayment = result.costInfo.total > 0;

  // Check subscription limits
  const limitCheck = checkSubscriptionLimits(billingContext);
  if (!limitCheck.withinLimits) {
    result.canPurchase = false;
    result.errors.push(...limitCheck.errors);
  }

  // Add warnings for additional costs
  if (result.requiresPayment && result.costInfo.total > 0) {
    result.warnings.push(
      `This purchase will incur a charge of $${result.costInfo.total.toFixed(2)}.`
    );
  }

  // Check wallet balance if payment is required
  if (result.requiresPayment && billingContext.walletBalance !== undefined) {
    if (billingContext.walletBalance < result.costInfo.totalCents) {
      result.canPurchase = false;
      result.errors.push(
        `Insufficient wallet balance. You need $${result.costInfo.total.toFixed(2)} but only have $${(billingContext.walletBalance / 100).toFixed(2)}.`
      );
    }
  }

  return result;
}

/**
 * Calculate purchase cost based on billing context
 * 
 * @param {BillingContext} billingContext - User's billing context
 * @param {PhoneNumber} phoneNumber - Phone number to purchase
 * @returns {Object} Cost calculation details
 */
export function calculatePurchaseCost(billingContext, phoneNumber) {
  const phoneNumbersIncluded = billingContext.phoneNumbersIncluded || 0;
  const phoneNumbersUsed = billingContext.phoneNumbersUsed || 0;
  
  // Base pricing from phone number
  const setupFee = phoneNumber.pricing?.setup?.amount || 0;
  const monthlyFee = phoneNumber.pricing?.monthly?.amount || 0;
  const isMonthlyIncluded = phoneNumber.pricing?.monthly?.isIncluded || false;

  let totalSetupFee = setupFee;
  let totalMonthlyFee = monthlyFee;
  let isIncludedInPlan = false;

  // Check if this number is included in the user's plan
  if (phoneNumbersUsed < phoneNumbersIncluded) {
    isIncludedInPlan = true;
    totalSetupFee = 0; // Setup fee is also covered by plan
    totalMonthlyFee = 0; // Monthly fee is covered by plan
  } else if (billingContext.additionalNumberCost) {
    // Use plan's additional number cost instead of Twilio pricing
    totalMonthlyFee = billingContext.additionalNumberCost.amount || monthlyFee;
  }

  const total = totalSetupFee + totalMonthlyFee;

  return {
    setupFee: totalSetupFee / 100,
    monthlyFee: totalMonthlyFee / 100,
    total: total / 100,
    totalCents: total,
    isIncludedInPlan,
    isMonthlyIncluded: isIncludedInPlan || isMonthlyIncluded,
    currency: phoneNumber.pricing?.currency || 'USD',
    breakdown: {
      setupFeeRaw: totalSetupFee,
      monthlyFeeRaw: totalMonthlyFee,
      totalRaw: total,
      planIncludesNumber: isIncludedInPlan,
      additionalNumberRate: billingContext.additionalNumberCost?.amount || 0
    }
  };
}

/**
 * Check subscription limits for phone number purchases
 * 
 * @param {BillingContext} billingContext - User's billing context
 * @returns {Object} Limit check result
 */
export function checkSubscriptionLimits(billingContext) {
  const result = {
    withinLimits: true,
    errors: [],
    warnings: [],
    limits: {
      included: billingContext.phoneNumbersIncluded || 0,
      used: billingContext.phoneNumbersUsed || 0,
      remaining: 0,
      canPurchaseMore: billingContext.canPurchaseMore !== false
    }
  };

  result.limits.remaining = Math.max(0, result.limits.included - result.limits.used);

  // Check if user can purchase more numbers
  if (!result.limits.canPurchaseMore) {
    result.withinLimits = false;
    result.errors.push('Your current plan does not allow additional phone number purchases.');
    return result;
  }

  // Check for hard limits (if any)
  if (billingContext.maxPhoneNumbers && billingContext.phoneNumbersUsed >= billingContext.maxPhoneNumbers) {
    result.withinLimits = false;
    result.errors.push(`You have reached the maximum limit of ${billingContext.maxPhoneNumbers} phone numbers for your account.`);
    return result;
  }

  // Add warnings for approaching limits
  if (result.limits.remaining === 1) {
    result.warnings.push('This is your last included phone number. Additional numbers will incur charges.');
  } else if (result.limits.remaining === 0 && result.limits.canPurchaseMore) {
    result.warnings.push('You have used all included phone numbers. This purchase will incur additional charges.');
  }

  return result;
}

/**
 * Format billing validation errors for display
 * 
 * @param {ValidationResult} validationResult - Validation result
 * @returns {Object} Formatted error information
 */
export function formatValidationErrors(validationResult) {
  return {
    hasErrors: validationResult.errors.length > 0,
    hasWarnings: validationResult.warnings.length > 0,
    primaryError: validationResult.errors[0] || null,
    allErrors: validationResult.errors,
    allWarnings: validationResult.warnings,
    canProceed: validationResult.canPurchase,
    requiresPayment: validationResult.requiresPayment,
    costSummary: validationResult.costInfo ? {
      total: validationResult.costInfo.total,
      currency: validationResult.costInfo.currency,
      isIncluded: validationResult.costInfo.isIncludedInPlan,
      breakdown: validationResult.costInfo.breakdown
    } : null
  };
}

/**
 * Generate purchase confirmation message based on validation
 * 
 * @param {ValidationResult} validationResult - Validation result
 * @param {PhoneNumber} phoneNumber - Phone number being purchased
 * @returns {string} Confirmation message
 */
export function generateConfirmationMessage(validationResult, phoneNumber) {
  if (!validationResult.canPurchase) {
    return 'This purchase cannot be completed due to billing restrictions.';
  }

  const costInfo = validationResult.costInfo;
  const phoneNumberFormatted = phoneNumber.friendlyName || phoneNumber.phoneNumber;

  if (costInfo.isIncludedInPlan) {
    return `Confirm purchase of ${phoneNumberFormatted}. This number is included in your plan at no additional cost.`;
  } else if (costInfo.total > 0) {
    return `Confirm purchase of ${phoneNumberFormatted} for $${costInfo.total.toFixed(2)}. This will be charged to your account.`;
  } else {
    return `Confirm purchase of ${phoneNumberFormatted}.`;
  }
}

/**
 * Validate billing context completeness
 * 
 * @param {BillingContext} billingContext - Billing context to validate
 * @returns {Object} Validation result
 */
export function validateBillingContext(billingContext) {
  const result = {
    isValid: true,
    missingFields: [],
    warnings: []
  };

  if (!billingContext) {
    result.isValid = false;
    result.missingFields.push('billingContext');
    return result;
  }

  // Check required fields
  const requiredFields = ['phoneNumbersIncluded', 'phoneNumbersUsed'];
  requiredFields.forEach(field => {
    if (billingContext[field] === undefined || billingContext[field] === null) {
      result.missingFields.push(field);
    }
  });

  // Check optional but recommended fields
  const recommendedFields = ['subscriptionId', 'planId', 'canPurchaseMore'];
  recommendedFields.forEach(field => {
    if (billingContext[field] === undefined || billingContext[field] === null) {
      result.warnings.push(`Missing recommended field: ${field}`);
    }
  });

  if (result.missingFields.length > 0) {
    result.isValid = false;
  }

  return result;
}
